import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/layout/Shell";
import { PageHeader, PrimaryButton, GhostButton, EmptyState, StatusBadge, formatCOP, Field, inputCls } from "@/components/common";
import { api } from "@/lib/api";
import { Plus, Users, Trash2, Store, Search, Eye, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyForm = {
  name: "", contact_name: "", email: "", phone: "",
  service: "marketing", status: "activo", billing_type: "mensual", monthly_value: 0, notes: "",
  nit: "", legal_rep: "", legal_rep_id: "", address: "",
};

export default function Clientes() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");
  const [billingFilter, setBillingFilter] = useState("todos");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Email inválido"); return; }
    try {
      const payload = { ...form, monthly_value: Number(form.monthly_value) || 0 };
      const { data } = await api.post("/clients", payload);
      toast.success("Cliente creado");
      setOpen(false); load();
      // Navigate to detail so user sees the full ficha
      setTimeout(() => navigate(`/clientes/${data.client_id}`), 100);
    } catch { toast.error("No se pudo guardar"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar el cliente "${c.name}"?`)) return;
    try { await api.delete(`/clients/${c.client_id}`); toast.success("Cliente eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const filtered = clients.filter(c => {
    if (billingFilter === "mensual" && (c.billing_type || "mensual") !== "mensual") return false;
    if (billingFilter === "unico" && c.billing_type !== "unico") return false;
    const qq = q.toLowerCase();
    return (
      c.name.toLowerCase().includes(qq) ||
      (c.contact_name || "").toLowerCase().includes(qq) ||
      (c.email || "").toLowerCase().includes(qq)
    );
  });

  return (
    <Shell>
      <PageHeader
        title="Clientes"
        subtitle={`${clients.length} restaurantes y marcas gestionadas`}
        action={
          <PrimaryButton data-testid="add-client-btn" onClick={openNew}>
            <Plus className="w-4 h-4" strokeWidth={2} /> Añadir cliente
          </PrimaryButton>
        }
      />

      {clients.length > 0 && (
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
            <input
              data-testid="search-clients-input"
              className={`${inputCls} pl-9`}
              placeholder="Buscar cliente..."
              value={q}
              onChange={(e)=>setQ(e.target.value)}
            />
          </div>
          <div className="inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
            {[
              { key: "todos", label: "Todos" },
              { key: "mensual", label: "Mensual" },
              { key: "unico", label: "Único" },
            ].map((f, i) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}-btn`}
                onClick={()=>setBillingFilter(f.key)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${i > 0 ? "border-l border-[#262A33]" : ""} ${
                  billingFilter === f.key
                    ? "bg-[#F97316]/10 text-[#F97316]"
                    : "text-[#9CA3AF] hover:text-[#F5F5F7]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-[#6B7280]">{filtered.length} de {clients.length}</span>
        </div>
      )}

      {loading ? (
        <div className="text-[#9CA3AF]">Cargando…</div>
      ) : clients.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Aún no tienes clientes"
          description="Añade el primero para empezar a gestionar tus restaurantes y marcas gastronómicas."
          action={<PrimaryButton data-testid="empty-add-client-btn" onClick={openNew}><Plus className="w-4 h-4"/> Añadir cliente</PrimaryButton>}
        />
      ) : (
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#262A33] text-[#9CA3AF]">
                  <th className="text-left font-medium px-5 py-3">Restaurante / Marca</th>
                  <th className="text-left font-medium px-5 py-3">Contacto</th>
                  <th className="text-left font-medium px-5 py-3">Servicio</th>
                  <th className="text-left font-medium px-5 py-3">Cobro</th>
                  <th className="text-left font-medium px-5 py-3">Estado</th>
                  <th className="text-right font-medium px-5 py-3">Valor</th>
                  <th className="text-right font-medium px-5 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.client_id} data-testid={`client-row-${c.client_id}`} className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors cursor-pointer" onClick={()=>navigate(`/clientes/${c.client_id}`)}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {c.logo_url ? (
                          <img src={c.logo_url} alt={c.name} className="w-9 h-9 rounded-xl object-cover border border-[#262A33]"/>
                        ) : (
                          <div className="w-9 h-9 rounded-xl bg-[#F97316]/10 flex items-center justify-center text-[#F97316] font-bold text-sm">
                            {c.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-[#F5F5F7] font-medium">{c.name}</div>
                          {c.notes && <div className="text-xs text-[#6B7280] truncate max-w-xs">{c.notes}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[#9CA3AF]">
                      <div>{c.contact_name || "—"}</div>
                      <div className="text-xs">{c.email || c.phone || ""}</div>
                    </td>
                    <td className="px-5 py-4 text-[#F5F5F7] capitalize">{c.service}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                        (c.billing_type === "unico")
                          ? "bg-purple-500/10 text-purple-300 border-purple-500/20"
                          : "bg-[#F97316]/10 text-[#F97316] border-[#F97316]/20"
                      }`}>
                        {c.billing_type === "unico" ? "Único" : "Mensual"}
                      </span>
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-4 text-right text-[#F5F5F7] font-medium">
                      {formatCOP(c.monthly_value)}
                      {c.billing_type !== "unico" && <span className="text-[10px] text-[#9CA3AF] ml-1">/mes</span>}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`view-client-${c.client_id}`}
                          onClick={(e)=>{ e.stopPropagation(); navigate(`/clientes/${c.client_id}`); }}
                          className="p-1.5 rounded-lg text-[#F97316] hover:bg-[#F97316]/10 transition-colors"
                          aria-label="Ver ficha"
                          title="Ver ficha"
                        >
                          <Eye className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          data-testid={`delete-client-${c.client_id}`}
                          onClick={(e)=>{ e.stopPropagation(); remove(c); }}
                          className="p-1.5 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          aria-label="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
              Nuevo cliente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nombre del restaurante / marca *">
              <input data-testid="client-name-input" required className={inputCls} value={form.name} onChange={e=>setForm({...form, name: e.target.value})} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Persona de contacto">
                <input className={inputCls} value={form.contact_name} onChange={e=>setForm({...form, contact_name: e.target.value})} />
              </Field>
              <Field label="Teléfono">
                <input className={inputCls} value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} />
              </Field>
            </div>
            <Field label="Email">
              <input data-testid="client-email-input" type="email" className={inputCls} value={form.email} onChange={e=>setForm({...form, email: e.target.value})} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="NIT"><input data-testid="client-nit-input" className={inputCls} value={form.nit} onChange={e=>setForm({...form, nit: e.target.value})} placeholder="900.000.000-0"/></Field>
              <Field label="Representante legal"><input className={inputCls} value={form.legal_rep} onChange={e=>setForm({...form, legal_rep: e.target.value})}/></Field>
              <Field label="Cédula rep. legal"><input className={inputCls} value={form.legal_rep_id} onChange={e=>setForm({...form, legal_rep_id: e.target.value})}/></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Servicio">
                <select className={inputCls} value={form.service} onChange={e=>setForm({...form, service: e.target.value})}>
                  <option value="marketing">Marketing digital</option>
                  <option value="consultoria">Consultoría</option>
                  <option value="ambos">Ambos</option>
                </select>
              </Field>
              <Field label="Estado del contrato">
                <select data-testid="client-status-select" className={inputCls} value={form.status} onChange={e=>setForm({...form, status: e.target.value})}>
                  <option value="activo">Activo</option>
                  <option value="pausado">Pausado</option>
                  <option value="finalizado">Finalizado</option>
                </select>
              </Field>
            </div>
            {form.status === "finalizado" && (
              <Field label="Motivo de baja" hint="Ayuda a entender por qué se fue el cliente y mejorar retención.">
                <textarea data-testid="churn-reason-input" rows={2} className={inputCls} placeholder="Ej: cambió de agencia, cerró el restaurante, precio, etc." value={form.churn_reason || ""} onChange={e=>setForm({...form, churn_reason: e.target.value})}/>
              </Field>
            )}
            <Field label="Tipo de cobro" hint="Los mensuales/quincenales entran en la generación automática de facturas recurrentes.">
              <div className="grid grid-cols-3 gap-2 mt-1">
                <button
                  type="button"
                  data-testid="billing-mensual-btn"
                  onClick={()=>setForm({...form, billing_type: "mensual"})}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    form.billing_type === "mensual"
                      ? "bg-[#F97316]/10 border-[#F97316] text-[#F97316]"
                      : "bg-[#0B0D12] border-[#262A33] text-[#9CA3AF] hover:text-[#F5F5F7]"
                  }`}
                >
                  Mensual
                </button>
                <button
                  type="button"
                  data-testid="billing-quincenal-btn"
                  onClick={()=>setForm({...form, billing_type: "quincenal"})}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    form.billing_type === "quincenal"
                      ? "bg-sky-500/10 border-sky-500 text-sky-400"
                      : "bg-[#0B0D12] border-[#262A33] text-[#9CA3AF] hover:text-[#F5F5F7]"
                  }`}
                >
                  Quincenal
                </button>
                <button
                  type="button"
                  data-testid="billing-unico-btn"
                  onClick={()=>setForm({...form, billing_type: "unico"})}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    form.billing_type === "unico"
                      ? "bg-purple-500/10 border-purple-500 text-purple-300"
                      : "bg-[#0B0D12] border-[#262A33] text-[#9CA3AF] hover:text-[#F5F5F7]"
                  }`}
                >
                  Pago único
                </button>
              </div>
            </Field>
            <Field label={form.billing_type === "unico" ? "Valor del proyecto (COP)" : form.billing_type === "quincenal" ? "Valor mensual (COP) - se divide en 2 quincenas" : "Valor mensual (COP)"}>
              <input data-testid="client-value-input" type="number" min="0" className={inputCls} value={form.monthly_value} onChange={e=>setForm({...form, monthly_value: e.target.value})} />
            </Field>
            <Field label="Notas internas">
              <textarea rows={3} className={inputCls} value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} />
            </Field>
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-client-btn" type="submit">Crear cliente</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
